// Expenses Excel workbook.
//
// One file, four sheets, because "what did we spend" gets asked four ways:
//   • Summary    — the window, the total, and how the file was filtered
//   • Expenses   — every single entry, so the rows can be re-sorted and audited
//   • By category— where the money actually went
//   • By period  — the day / week / month trend already on screen
//
// The Expenses sheet is the whole filtered set, never a truncated page: a
// workbook whose rows add up to less than its own Summary total is worse than
// no workbook. The caller passes `full: true` data and we assert it below.

import * as XLSX from 'xlsx';

const aoa = (rows) => XLSX.utils.aoa_to_sheet(rows);
const n = (v) => Number(v || 0);

const save = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31)));
  XLSX.writeFile(wb, filename);
};

// What the chosen filter is called, for the Summary sheet and the filename.
export const rangeLabel = ({ preset, from, to }) => {
  if (preset === 'custom' || (!preset && (from || to))) {
    if (from && to) return `${from} to ${to}`;
    if (from) return `From ${from}`;
    if (to) return `Up to ${to}`;
    return 'All time';
  }
  return {
    today: 'Today',
    week: 'This week',
    month: 'This month',
    year: 'This year',
    all: 'All time',
  }[preset] || 'All time';
};

const fileStamp = ({ preset, from, to }) => {
  if (preset === 'custom' || (!preset && (from || to))) {
    return `${from || 'start'}_to_${to || 'today'}`;
  }
  return preset || 'all';
};

/**
 * @param report  the /expenses/report/ payload fetched with full=1
 * @param filters { preset, from, to, category, group }
 */
export const downloadExpensesWorkbook = (report, filters = {}) => {
  const label = rangeLabel(filters);
  const items = Array.isArray(report?.items) ? report.items : [];
  const byCategory = Array.isArray(report?.by_category) ? report.by_category : [];
  const series = Array.isArray(report?.series) ? report.series : [];

  const total = n(report?.total);
  const rowsTotal = items.reduce((sum, r) => sum + n(r.amount), 0);
  // If the backend still truncated, say so IN the file rather than shipping a
  // workbook that silently disagrees with itself.
  const truncated = report?.items_truncated
    || (report?.items_total != null && items.length < Number(report.items_total));

  const summary = [
    ['Expenses report'],
    [],
    ['Period', label],
    ['From', report?.from || '—'],
    ['To', report?.to || '—'],
    ['Category filter', filters.category || report?.title || 'All categories'],
    [],
    ['Total spent', total],
    ['Number of expenses', n(report?.count)],
    ['Rows in this file', items.length],
    ['Rows add up to', rowsTotal],
  ];
  if (truncated) {
    summary.push(
      [],
      ['WARNING', `This file contains ${items.length} of ${report?.items_total} expenses.`],
      ['', 'The rows below do NOT add up to the total above. Narrow the date range and download again.'],
    );
  } else if (Math.round(rowsTotal) !== Math.round(total)) {
    summary.push(
      [],
      ['WARNING', 'The rows do not add up to the total — report this.'],
    );
  }

  const expenses = [
    ['Date', 'Category', 'Amount', 'Added by'],
    ...items.map((r) => [r.date_display || r.date || '', r.title || '', n(r.amount), r.added_by || '']),
  ];
  if (items.length) expenses.push(['', 'TOTAL', rowsTotal, '']);

  const categories = [
    ['Category', 'Number of expenses', 'Total spent', 'Share of spend'],
    ...byCategory.map((c) => [
      c.title || '',
      n(c.count),
      n(c.total),
      total ? Number((n(c.total) / total).toFixed(4)) : 0,
    ]),
  ];
  if (byCategory.length) categories.push(['TOTAL', n(report?.count), total, total ? 1 : 0]);

  const groupName = { day: 'Day', week: 'Week', month: 'Month' }[report?.group] || 'Day';
  const periods = [
    [groupName, 'Number of expenses', 'Total spent'],
    ...series.map((s) => [s.label || s.key || '', n(s.count), n(s.total)]),
  ];
  if (series.length) periods.push(['TOTAL', n(report?.count), total]);

  save(
    [
      ['Summary', aoa(summary)],
      ['Expenses', aoa(expenses)],
      ['By category', aoa(categories)],
      [`By ${groupName.toLowerCase()}`, aoa(periods)],
    ],
    `expenses_${fileStamp(filters)}.xlsx`,
  );
};
